import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './appeals.css';
import { getAppealSettings, useAppealsVersion } from './store';
import { CHANNELS_IN_ORDER, PAYER_PLAN_RULES } from './payerRules';
import type { SettingsPayerRow } from './types';
import { SectionHeader } from '../settings-d/primitives';
import {
  NumberSetting,
  SETTINGS_TODAY,
  SettingsChangeFooter,
  recordSettingsChange,
  useCanEditSettings,
} from '../settings-d/settingsEdit';
import {
  AIM_DAYS,
  MAX_WINDOW_DAYS,
  MIN_WINDOW_DAYS,
  daysWord,
  getAppealWindow,
  setAppealWindow,
  useAppealWindows,
} from './appealWindows';

/**
 * Settings › Appeals — direction D (00-README "Also change on existing RCM
 * settings pages"). This page now keeps only "How each payer gets your
 * appeals". Autopilot moved to /settings/autopilot; the authorization, who
 * signs and signatures to /settings/signatures; letterhead and return address
 * to /settings/fax-mail. A one-line link is left where each one was.
 *
 * Mounted as the appeals settings slot inside the settings-d shell, so it uses
 * that shell's `setd-*` chrome.
 */

/** A one-line "moved to another settings page" row. */
function MovedRow({ title, where, to }: { title: string; where: string; to: string }) {
  return (
    <div className="setd-row">
      <div style={{ minWidth: 0 }}>
        <div className="setd-row-title">{title}</div>
        <div className="setd-row-help">Now in {where}.</div>
      </div>
      <div className="setd-row-right">
        <Link to={to} className="setd-link">
          Open
        </Link>
      </div>
    </div>
  );
}

/**
 * The APPEAL WINDOW cell (D229). Everyone sees "180 days from the decision" and where it
 * came from. Owners and admins click the number to change it (1–730); it saves on blur or
 * Enter, and the meta line then reads "Set by your practice · Oct 22".
 */
function AppealWindowCell({ row, canEdit }: { row: SettingsPayerRow; canEdit: boolean }) {
  const windows = useAppealWindows();
  const [editing, setEditing] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const before = useRef(0);
  const rule = PAYER_PLAN_RULES.find((r) => r.key === row.key);
  const w = windows[row.key] ?? getAppealWindow(row.key);
  const suffix = (rule?.deadlineBasis ?? '').replace(/^\d+\s+days?/, '');
  const researched = row.source.startsWith('From ');
  // D241/D256: a payer without a researched window never shows 30 days as its deadline.
  const unconfirmed = !w.confirmed && !w.setByPractice;
  const meta = w.setByPractice
    ? `Set by your practice · ${SETTINGS_TODAY}`
    : unconfirmed
      ? `ELVA’s own aim, not ${row.payerPlan.split(' ')[0] ?? 'the payer'}’s deadline`
      : researched
        ? `from ${row.source.slice(5)}`
        : 'ELVA’s default';

  useEffect(() => {
    if (editing) wrap.current?.querySelector('input')?.focus();
  }, [editing]);
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  const text = unconfirmed ? `Not confirmed · ELVA aims to send within ${daysWord(AIM_DAYS)}` : `${daysWord(w.days)}${suffix}`;
  return (
    <>
      {canEdit ? (
        editing ? (
          <span
            ref={wrap}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditing(false);
            }}
            onBlur={() => {
              // Let the field commit first; stay open a moment when it has "Saved" or a message to show.
              setTimeout(() => {
                const msg = wrap.current?.querySelector('[role="alert"]');
                const changed = getAppealWindow(row.key).days !== before.current;
                if (!msg && !changed) {
                  setEditing(false);
                  return;
                }
                closeTimer.current = setTimeout(() => {
                  setEditing(false);
                }, 1600);
              }, 0);
            }}
          >
            <NumberSetting
              label={`Appeal window for ${row.payerPlan}, in days`}
              value={w.days}
              min={MIN_WINDOW_DAYS}
              max={MAX_WINDOW_DAYS}
              unit="days"
              suffix=" days"
              onCommit={(n) => {
                setAppealWindow(row.key, n);
                recordSettingsChange('appeal-windows', `${row.payerPlan} appeal window`);
              }}
            />
          </span>
        ) : (
          <button
            type="button"
            className="setd-ed-link"
            aria-label={`${text}. Change the appeal window for ${row.payerPlan}`}
            onClick={() => {
              if (closeTimer.current) clearTimeout(closeTimer.current);
              before.current = w.days;
              setEditing(true);
            }}
          >
            {text}
          </button>
        )
      ) : (
        <span>{text}</span>
      )}
      <div className="apl-meta" style={{ marginTop: 2 }}>{meta}</div>
    </>
  );
}

export function AppealsSettings() {
  useAppealsVersion();
  const canEdit = useCanEditSettings();
  const s = getAppealSettings();
  const [toast, setToast] = useState('');

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => {
      setToast('');
    }, 4000);
  };

  return (
    <>
      {/* Moved — autopilot + authorization/who-signs/signatures */}
      <section className="setd-card">
        <SectionHeader n="" title="Moved to other settings" />
        <MovedRow title="Send appeals on autopilot" where="Autopilot" to="/settings/autopilot" />
        <MovedRow
          title="Authorization, who signs, and signatures on file"
          where="Signatures"
          to="/settings/signatures"
        />
      </section>

      {/* How each payer gets your appeals — the one card that stays here */}
      <section className="setd-card">
        <SectionHeader n="" title="How each payer gets your appeals" />
        <div className="apl-tbl-wrap" style={{ margin: '4px 0 0' }}>
          <table className="apl-tbl">
            <thead>
              <tr>
                <th>Payer · plan</th>
                <th>What the payer accepts</th>
                <th>Appeal window</th>
                <th>ELVA sends by</th>
                <th>If that fails</th>
              </tr>
            </thead>
            <tbody>
              {s.payers.map((p) => (
                <tr key={p.key}>
                  <td style={{ fontWeight: 600 }}>{p.payerPlan}</td>
                  <td>
                    {p.accepts}
                    {p.firstStep ? <span className="apl-badge b-mute" style={{ marginLeft: 6 }}>Adjustment first</span> : null}
                    {p.required ? <span className="apl-badge b-mute" style={{ marginLeft: 6 }}>Required by payer</span> : null}
                    <div className="apl-meta" style={{ marginTop: 2 }}>{p.source} · checked {p.checkedOn}</div>
                  </td>
                  <td>
                    <AppealWindowCell row={p} canEdit={canEdit} />
                  </td>
                  <td>
                    <select
                      className="apl-select"
                      defaultValue={p.first}
                      disabled={Boolean(p.required) || !canEdit}
                      aria-label={`How ELVA sends ${p.payerPlan} appeals`}
                      onChange={() => {
                        flash(`Updated how ${p.payerPlan} appeals go out.`);
                      }}
                    >
                      {CHANNELS_IN_ORDER.map((c) => (
                        <option key={c.id} value={c.id} disabled={!p.acceptedChannels.includes(c.id)}>{c.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="apl-meta">{p.fallback}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ margin: '10px 20px 0' }}>
          <SettingsChangeFooter id="appeal-windows" initial="Oct 1" className="apl-meta" />
        </div>
        <ul className="apl-meta" style={{ margin: '10px 20px 4px', paddingLeft: 18 }}>
          <li>When a payer&rsquo;s window isn&rsquo;t confirmed, ELVA aims to send within 30 days and never shows that as the payer&rsquo;s deadline. Set a number to use it as your practice&rsquo;s deadline.</li>
          <li>When a deadline is close, ELVA skips mail and uses a faster way the payer accepts.</li>
          <li>A phone call isn&rsquo;t a written appeal.</li>
          <li>ELVA re-checks payer rules and switches to the next way — and says so here — when a payer stops accepting one.</li>
        </ul>
      </section>

      {/* Moved — letterhead and return address */}
      <section className="setd-card">
        <SectionHeader n="" title="Letterhead and return address" />
        <div className="setd-row">
          <div style={{ minWidth: 0 }}>
            <div className="setd-row-help">On every letter, fax cover and envelope. Now set in Fax &amp; mail.</div>
          </div>
          <div className="setd-row-right">
            <Link to="/settings/fax-mail" className="setd-link">
              Open
            </Link>
          </div>
        </div>
      </section>

      {toast ? (
        <div className="apl-toasts">
          <div className="apl-toast" role="status">
            <span>{toast}</span>
          </div>
        </div>
      ) : null}
    </>
  );
}
